import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { ArrowLeft, Save, Camera, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DELIVERY_OPTIONS, isNoDocketMode, deliveryLabel } from '@/lib/deliveryModes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScanDispatchMobile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [entityData, setEntityData] = useState(null);
  const [entityType, setEntityType] = useState('member');
  const [courierInfo, setCourierInfo] = useState({ type: '', tracking_url: '' });
  const [scannerActive, setScannerActive] = useState(false);
  const [scanMode, setScanMode] = useState(''); // 'member_id' or 'docket_no'
  const scannerRef = useRef(null);

  const getCurrentMonth = () => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[new Date().getMonth()];
  };

  const [formData, setFormData] = useState({
    member_id: '',
    name: '',
    docket_no: '',
    magazine: '',
    courier_type: '',
    month: getCurrentMonth(),
    year: new Date().getFullYear().toString()
  });
  const noDocketMode = isNoDocketMode(formData.courier_type);

  useEffect(() => {
    if (formData.member_id) {
      fetchEntityDetails(formData.member_id);
    }
  }, [formData.member_id, entityType]);

  useEffect(() => {
    if (formData.docket_no) {
      detectCourierType(formData.docket_no);
    }
  }, [formData.docket_no]);

  const fetchEntityDetails = async (id) => {
    try {
      const cleanId = id.trim();
      if (!cleanId) return;

      let response;
      try {
        response = await axios.get(`${API}/members/${cleanId}`);
        setEntityType('member');
      } catch (memberError) {
        try {
          response = await axios.get(`${API}/advertisers/${cleanId}`);
          setEntityType('advertiser');
        } catch (advertiserError) {
          toast.error('ID not found in members or advertisers');
          setEntityData(null);
          setFormData({ ...formData, name: '' });
          return;
        }
      }

      setEntityData(response.data);
      setFormData({ ...formData, name: response.data.name || response.data.company_name || '' });
    } catch (error) {
      console.error('Error fetching entity details:', error);
      setEntityData(null);
      setFormData({ ...formData, name: '' });
    }
  };

  const detectCourierType = (docketNo) => {
    const docket = docketNo.toUpperCase();
    
    if (docket.startsWith('1') && docket.length === 13) {
      setCourierInfo({
        type: 'Blue Dart',
        tracking_url: `https://www.bluedart.com/tracking?trackFor=0&trackNo=${docketNo}`
      });
    } else if (docket.startsWith('EE') || docket.startsWith('RA')) {
      setCourierInfo({
        type: 'India Post',
        tracking_url: `https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx?cno=${docketNo}`
      });
    } else {
      setCourierInfo({ type: '', tracking_url: '' });
    }
  };

  const startScanner = (mode) => {
    setScanMode(mode);
    setScannerActive(true);
    
    setTimeout(() => {
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };

      const html5QrcodeScanner = new Html5QrcodeScanner(
        "qr-reader",
        config,
        false
      );

      html5QrcodeScanner.render(
        (decodedText) => {
          // Success callback
          if (mode === 'member_id') {
            setFormData(prev => ({ ...prev, member_id: decodedText }));
            toast.success(`Scanned Member ID: ${decodedText}`);
          } else {
            setFormData(prev => ({ ...prev, docket_no: decodedText }));
            toast.success(`Scanned Docket No: ${decodedText}`);
          }
          stopScanner();
        },
        (errorMessage) => {
          // Error callback - can be ignored for continuous scanning
        }
      );

      scannerRef.current = html5QrcodeScanner;
    }, 100);
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch(err => console.log(err));
      scannerRef.current = null;
    }
    setScannerActive(false);
    setScanMode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const noDocket = isNoDocketMode(formData.courier_type);
    if (!formData.member_id || !formData.magazine || !formData.courier_type ||
        (!noDocket && !formData.docket_no)) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const username = localStorage.getItem('username') || 'Mobile Scanner';

      const autoDocket = noDocket
        ? (formData.courier_type === 'By Hand' ? 'BH' :
           formData.courier_type === 'By Post' ? 'BP' : 'EMAG')
        : formData.docket_no;

      const dispatchData = {
        member_id: formData.member_id.toUpperCase(),
        name: formData.name,
        docket_no: autoDocket,
        magazine: formData.magazine,
        month: formData.month,
        year: formData.year,
        courier_type: deliveryLabel(formData.courier_type),
        tracking_url: courierInfo.tracking_url || '',
        dispatched_by: username
      };

      await axios.post(`${API}/dispatches`, dispatchData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Dispatch recorded successfully!');
      
      // Reset form
      setFormData({
        member_id: '',
        name: '',
        docket_no: '',
        magazine: '',
        courier_type: '',
        month: getCurrentMonth(),
        year: new Date().getFullYear().toString()
      });
      setEntityData(null);
      setCourierInfo({ type: '', tracking_url: '' });
    } catch (error) {
      console.error('Error saving dispatch:', error);
      toast.error(error.response?.data?.detail || 'Failed to save dispatch');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center space-x-4 mb-8">
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2" size={18} />
            Back
          </Button>
          <h1 className="text-3xl font-bold" style={{ color: '#7c3aed' }}>
            Scan Dispatch (Mobile)
          </h1>
        </div>

        {scannerActive && (
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col items-center justify-center p-4">
            <div className="bg-white rounded-lg p-4 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  Scanning {scanMode === 'member_id' ? 'Member ID (QR)' : 'Docket No (Barcode)'}
                </h2>
                <Button variant="outline" onClick={stopScanner} size="sm">
                  <X size={18} />
                </Button>
              </div>
              <div id="qr-reader" className="w-full"></div>
            </div>
          </div>
        )}

        <div className="form-section">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Entity Type</Label>
                <Select value={entityType} onValueChange={setEntityType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="advertiser">Advertiser</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Member/Advertiser ID *</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.member_id}
                    onChange={(e) => setFormData({ ...formData, member_id: e.target.value.toUpperCase() })}
                    placeholder="Enter or scan ID"
                    required
                  />
                  <Button type="button" onClick={() => startScanner('member_id')} variant="outline" size="icon">
                    <Camera size={18} />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Auto-filled" />
              </div>

              <div className="space-y-2">
                <Label>Courier Type *</Label>
                <Select value={deliveryLabel(formData.courier_type)} onValueChange={(value) => setFormData({ ...formData, courier_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select courier" />
                  </SelectTrigger>
                  <SelectContent>
                    {DELIVERY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {noDocketMode && (
                  <p className="text-xs text-gray-500">No docket needed for {formData.courier_type}</p>
                )}
              </div>

              {!noDocketMode && (
                <div className="space-y-2">
                  <Label>Docket Number *</Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.docket_no}
                      onChange={(e) => setFormData({ ...formData, docket_no: e.target.value })}
                      placeholder="Enter or scan docket"
                      required
                    />
                    <Button type="button" onClick={() => startScanner('docket_no')} variant="outline" size="icon">
                      <Camera size={18} />
                    </Button>
                  </div>
                  {courierInfo.type && (
                    <p className="text-xs text-green-600">Auto-detected: {courierInfo.type}</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Magazine *</Label>
                <Select value={formData.magazine} onValueChange={(value) => setFormData({ ...formData, magazine: value })} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select magazine" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                    <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Month *</Label>
                <Select value={formData.month} onValueChange={(value) => setFormData({ ...formData, month: value })} required>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(month => (
                      <SelectItem key={month} value={month}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Year *</Label>
                <Select value={formData.year} onValueChange={(value) => setFormData({ ...formData, year: value })} required>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['2024', '2025', '2026'].map(year => (
                      <SelectItem key={year} value={year}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex space-x-4 mt-8">
              <Button type="submit" disabled={loading} className="btn-primary" style={{ backgroundColor: '#7c3aed' }}>
                <Save className="mr-2" size={18} />
                {loading ? 'Saving...' : 'SAVE DISPATCH'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ScanDispatchMobile;
